import { useNotification } from "@refinedev/core";
import { List } from "@refinedev/antd";
import { Button, Select, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useState, type FC } from "react";
import { axiosInstance } from "../../api/client";
import type { AcfObjectType, DeployResult } from "../../types";
import { DeployModal } from "./_components/deploy-modal";
import { DeployResultsModal } from "./_components/deploy-results-modal";
import { JsonViewerModal } from "./_components/json-viewer-modal";
import { ProjectSelect } from "./_components/project-select";
import { TargetsModal } from "./_components/targets-modal";
import { useSelectedProjectIds } from "./_components/use-selected-project-ids";
import { ACF_TYPE_COLOR, ACF_TYPE_LABEL } from "./constants";
import type { AcfRow, DeployResultRow } from "./types";
import { useAcfData } from "./use-acf-data";

dayjs.extend(relativeTime);

const { Text } = Typography;

const ACF_TYPE_OPTIONS: { label: string; value: AcfObjectType }[] = (
  Object.keys(ACF_TYPE_LABEL) as AcfObjectType[]
).map((type) => ({ label: ACF_TYPE_LABEL[type], value: type }));

export const AcfList: FC = () => {
  const { open: notify } = useNotification();

  const { projects, rows, targetsByObject, loading, refetch } = useAcfData();
  const [selectedProjectIds] = useSelectedProjectIds();
  const [selectedTypes, setSelectedTypes] = useState<AcfObjectType[]>([]);

  const [jsonViewer, setJsonViewer] = useState<AcfRow | null>(null);
  const [deployModal, setDeployModal] = useState<{ objectIds: string[] } | null>(null);
  const [deploying, setDeploying] = useState(false);
  const [targetsModal, setTargetsModal] = useState<AcfRow | null>(null);
  const [deployResults, setDeployResults] = useState<DeployResultRow[] | null>(null);

  const openDeploy = (objectIds: string[]) => {
    setDeployModal({ objectIds });
  };

  const environmentLabel = (environmentId: string): string => {
    for (const proj of projects) {
      const env = proj.environments.find((e) => e.id === environmentId);
      if (env) return `${proj.name} / ${env.name}`;
    }
    return environmentId;
  };

  const confirmDeploy = async (selectedEnvironmentIds: string[]) => {
    if (!deployModal) return;
    setDeploying(true);

    // One call per object, looped client-side: deploy is atomic per (object, environment) on
    // the api, "deploy all" is a console-side convenience, same as the snippets catalog.
    const results: DeployResultRow[] = [];
    for (const objectId of deployModal.objectIds) {
      const row = rows.find((r) => r.id === objectId);
      if (!row) continue;
      const { data } = await axiosInstance.post<DeployResult[]>(`/projects/${row.projectId}/acf/${objectId}/deploy`, {
        targetEnvironmentIds: selectedEnvironmentIds,
      });
      results.push(
        ...data.map((result) => ({
          ...result,
          objectId,
          objectLabel: row.title || row.key,
          environmentLabel: environmentLabel(result.environmentId),
        }))
      );
    }

    setDeploying(false);
    setDeployModal(null);

    const failures = results.filter((r) => r.status === "failure");
    if (results.length > 1 || failures.length > 0) {
      setDeployResults(results);
    } else {
      notify?.({ type: "success", message: "Deployed successfully" });
    }

    void refetch();
  };

  const removeTarget = async (row: AcfRow, environmentId: string, removeFromSite: boolean) => {
    await axiosInstance.delete(`/projects/${row.projectId}/acf/${row.id}/deployments/${environmentId}`, { data: { removeFromSite } });
    void refetch();
  };

  const filteredRows = rows
    .filter((row) => selectedProjectIds.length === 0 || selectedProjectIds.includes(row.projectId))
    .filter((row) => selectedTypes.length === 0 || selectedTypes.includes(row.type));
  const activeFilteredRows = filteredRows.filter((r) => !r.deletedAt);

  return (
    <List title="ACF" canCreate={false}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button type="primary" disabled={activeFilteredRows.length === 0} onClick={() => openDeploy(activeFilteredRows.map((r) => r.id))}>
          Deploy all
        </Button>
        <ProjectSelect projects={projects} />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 200 }}
          placeholder="Filter by type"
          value={selectedTypes}
          onChange={setSelectedTypes}
          options={ACF_TYPE_OPTIONS}
        />
      </Space>

      <Table<AcfRow>
        dataSource={filteredRows}
        rowKey="id"
        loading={loading}
        scroll={{ x: "max-content" }}
        pagination={filteredRows.length > 10 ? { pageSize: 10 } : false}
        locale={{
          emptyText: (
            <div style={{ padding: "32px 0" }}>
              <p style={{ fontWeight: 600, marginBottom: 4 }}>No published ACF objects yet</p>
              <p style={{ color: "#888", margin: 0 }}>
                Run <Typography.Text code>lps acf publish</Typography.Text> from a project to share its field groups, post types,
                taxonomies, or options pages here.
              </p>
            </div>
          ),
        }}
      >
        <Table.Column<AcfRow> title="Project" render={(_, row) => row.projectName} />
        <Table.Column<AcfRow> title="Type" render={(_, row) => <Tag color={ACF_TYPE_COLOR[row.type]}>{ACF_TYPE_LABEL[row.type]}</Tag>} />
        <Table.Column<AcfRow>
          title="Name"
          render={(_, row) => (
            <Space>
              {row.title || row.key}
              {row.deletedAt && <Tag color="orange">Removed from repo, still on {targetsByObject[row.id]?.length ?? 0} site(s)</Tag>}
            </Space>
          )}
        />
        <Table.Column<AcfRow>
          title="Status"
          render={(_, row) => {
            const targets = targetsByObject[row.id] ?? [];
            if (targets.length === 0) return <Text type="secondary">Never deployed</Text>;
            const upToDate = targets.filter((t) => t.upToDate).length;
            return (
              <Text>
                {upToDate}/{targets.length} up to date
              </Text>
            );
          }}
        />
        <Table.Column<AcfRow> title="Last updated" render={(_, row) => dayjs(row.updatedAt).fromNow()} />
        <Table.Column<AcfRow>
          title=""
          align="right"
          render={(_, row) => (
            <Space size="small">
              <Button size="small" onClick={() => setJsonViewer(row)}>
                View JSON
              </Button>
              {!row.deletedAt && (
                <Button size="small" onClick={() => openDeploy([row.id])}>
                  Deploy
                </Button>
              )}
              {(targetsByObject[row.id]?.length ?? 0) > 0 && (
                <Button size="small" onClick={() => setTargetsModal(row)}>
                  {row.deletedAt ? "Clean up" : "Manage targets"}
                </Button>
              )}
            </Space>
          )}
        />
      </Table>

      <JsonViewerModal title={jsonViewer?.title || jsonViewer?.key || ""} data={jsonViewer?.data ?? null} onClose={() => setJsonViewer(null)} />

      <DeployModal
        deployModal={deployModal}
        rows={rows}
        projects={projects}
        targetsByObject={targetsByObject}
        deploying={deploying}
        onCancel={() => setDeployModal(null)}
        onConfirm={(environmentIds) => void confirmDeploy(environmentIds)}
      />

      <TargetsModal
        object={targetsModal}
        targets={targetsModal ? targetsByObject[targetsModal.id] ?? [] : []}
        onClose={() => setTargetsModal(null)}
        onRemoveTarget={(environmentId, removeFromSite) => {
          if (targetsModal) void removeTarget(targetsModal, environmentId, removeFromSite);
        }}
      />

      <DeployResultsModal results={deployResults} onClose={() => setDeployResults(null)} />
    </List>
  );
};
